import { useEffect, useState } from 'react';

/**
 * Whether an element's content overflows the box it has been clamped to.
 *
 * With a line clamp applied, `scrollHeight > clientHeight` is the whole question, so no
 * line height constant is needed and the answer stays correct at any type scale. That
 * is the one thing this does differently from `ExpandableTextContainer` on PDP, which
 * divides a measured height by a hard-coded 21px or 24px depending on the viewport.
 *
 * Returns the element's ref alongside the answer, and that ref is a callback rather than
 * a `useRef` object on purpose: a ref object's identity never changes, so a hook keyed on
 * one cannot tell when the node it should be measuring arrives or is replaced. A review
 * whose text was absent at mount would then never be measured at all, and would render
 * clipped with no way to read the rest.
 *
 * A `ResizeObserver` re-measures when the element changes size, which is the
 * layout-agnostic version of watching the window: what matters is the width the card's
 * container gave it, not the width of the viewport. Environments without one, jsdom
 * included, still get the measurement taken when the element arrives.
 *
 * Server-rendered output is always "not truncated", since nothing has been laid out
 * yet. A consumer that reserves the space either way, as `ReviewPreviewCard` does, sees
 * the link appear on hydration without anything moving around it.
 *
 * `content` is whatever the element renders, passed so a change to it re-measures. The
 * observer alone cannot cover that case: swapping the text of a clamped element whose
 * old text exactly filled the clamp leaves the box the same size, so no resize fires,
 * and the new, longer text would keep the old answer and render clipped with no way
 * through to the rest.
 */
export const useIsTruncated = (content?: unknown) => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (!element) {
      return undefined;
    }

    const measure = () => setIsTruncated(element.scrollHeight > element.clientHeight);

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(measure);

    observer.observe(element);

    return () => observer.disconnect();
  }, [element, content]);

  /* `setElement` is a state setter, so its identity is stable and React neither detaches
     nor reattaches the ref between renders. */
  return { isTruncated, ref: setElement };
};
